import React, { useState, memo, useRef } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ScrollView,
  Platform,
} from 'react-native';
import { Text } from '@/components/common/text';
import { AnchoredDropdownItem } from '@/components/common/admin/desktop/dropdown/AnchoredDropdownItem';
import { SVG_COMPONENTS } from '@/assets/admin';
import { COMMUNITY_RESOURCE_ICONS, CommunityResourceIconKey } from '@/assets/admin/CommunityResourceIcons';
import { resioIconOptionMappings } from '@/types/admin/resources';
import { AnchoredDropdown } from '@/components/common/admin/desktop/dropdown/AnchoredDropdown';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { GlassCard } from '@/components/shared/redesign';

interface IconSelectorProps {
  selectedIcon: string;
  onIconSelect: (iconKey: string) => void;
  error?: string;
}

export const IconSelector = memo<IconSelectorProps>(({
  selectedIcon,
  onIconSelect,
  error,
}) => {
  const { redesign, semantic } = useAdminColors();
  const [showModal, setShowModal] = useState(false);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<AnchorRect | null>(null);
  const anchorRef = useRef<any>(null);

  // Get the selected icon component
  const SelectedIconComponent = COMMUNITY_RESOURCE_ICONS[selectedIcon as CommunityResourceIconKey] || COMMUNITY_RESOURCE_ICONS.photoIcon;
  const selectedIconLabel = resioIconOptionMappings[selectedIcon as keyof typeof resioIconOptionMappings] || 'Photo';

  const handleIconSelect = (iconKey: string) => {
    onIconSelect(iconKey);
    setShowModal(false);
    setDropdownOpen(false);
  };

  const renderIconItem = (iconKey: string, iconLabel: string) => {
    const IconComponent = COMMUNITY_RESOURCE_ICONS[iconKey as CommunityResourceIconKey];
    const isSelected = iconKey === selectedIcon;

    if (!IconComponent) return null;

    return (
      <TouchableOpacity
        key={iconKey}
        style={[
          styles.iconItem,
          {
            backgroundColor: redesign.glass.buttonBg,
            borderColor: isSelected ? redesign.accent.blueBg : redesign.card.border,
            borderWidth: isSelected ? 1 : 1,
          },
          isSelected && { backgroundColor: redesign.accent.blueBgLight },
        ]}
        onPress={() => handleIconSelect(iconKey)}
      >
        <View
          style={[
            styles.iconContainer,
            {
              backgroundColor: isSelected ? redesign.accent.blueBgLight : redesign.glass.buttonBg,
            },
          ]}
        >
          <IconComponent
            width={24}
            height={24}
            color={isSelected ? redesign.accent.blueBg : redesign.text.primary}
          />
        </View>
        <Text
          style={[
            styles.iconLabel,
            { color: isSelected ? semantic.text.link : redesign.text.muted },
            isSelected && styles.iconLabelSelected,
          ]}
        >
          {iconLabel}
        </Text>
      </TouchableOpacity>
    );
  };

  const iconEntries = Object.entries(resioIconOptionMappings);

  // Create rows of icons (2 per row)
  const iconRows = [];
  for (let i = 0; i < iconEntries.length; i += 2) {
    const row = iconEntries.slice(i, i + 2);
    iconRows.push(row);
  }

  return (
    <View style={styles.container}>
      <Text style={[styles.label, { color: redesign.text.primary }]}>
        Icon <Text style={{ color: semantic.status.error }}>*</Text>
      </Text>

      <View>
        <TouchableOpacity
          ref={anchorRef}
          style={[
            styles.selector,
            {
              backgroundColor: redesign.glass.buttonBg,
              borderColor: error ? semantic.status.error : redesign.card.border,
            },
          ]}
          onPress={() => {
            if (Platform.OS === 'web' && anchorRef.current) {
              const element: any = anchorRef.current as any;
              const rect = element?.getBoundingClientRect?.();
              if (rect) {
                setAnchorRect({ x: rect.left, y: rect.top, w: rect.width, h: rect.height });
                setDropdownOpen(true);
                return;
              }
            }
            setShowModal(true);
          }}
        >
        <View style={[styles.selectedIconContainer, { backgroundColor: redesign.glass.buttonBg }]}>
          <SelectedIconComponent
            width={24}
            height={24}
            color={redesign.text.primary}
          />
        </View>
        <Text style={[styles.selectedLabel, { color: redesign.text.primary }]}>{selectedIconLabel}</Text>
        <SVG_COMPONENTS.CHEVRON_DOWN
          width={20}
          height={20}
          color={redesign.text.muted}
        />
        </TouchableOpacity>
      </View>

      {error ? <Text style={[styles.errorText, { color: semantic.status.error }]}>{error}</Text> : null}

      {/* Desktop web anchored dropdown */}
      <AnchoredDropdown
        isOpen={isDropdownOpen}
        anchor={anchorRect}
        width={anchorRect?.w ?? 300}
        maxHeight={360}
        onRequestClose={() => setDropdownOpen(false)}
      >
        <GlassCard padding={0} borderRadius={8} style={styles.dropdownCard}>
          <ScrollView style={{ maxHeight: 360 }} contentContainerStyle={{ paddingVertical: 8 }}>
            {iconEntries.map(([iconKey, iconLabel]) => {
              const IconComponent = COMMUNITY_RESOURCE_ICONS[iconKey as CommunityResourceIconKey];
              const isSelected = iconKey === selectedIcon;
              if (!IconComponent) return null;
              return (
                <AnchoredDropdownItem
                  key={`icon-${iconKey}`}
                  selected={isSelected}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    paddingVertical: 10,
                    paddingHorizontal: 12,
                    backgroundColor: isSelected ? redesign.accent.blueBgLight : 'transparent',
                  }}
                  onPress={() => handleIconSelect(iconKey)}
                >
                  <View
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 14,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: redesign.glass.buttonBg,
                      marginRight: 10,
                    }}
                  >
                    <IconComponent width={18} height={18} color={isSelected ? redesign.accent.blueBg : redesign.text.primary} />
                  </View>
                  <Text style={{ color: redesign.text.primary }}>{iconLabel}</Text>
                </AnchoredDropdownItem>
              );
            })}
          </ScrollView>
        </GlassCard>
      </AnchoredDropdown>

      <Modal
        visible={showModal}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setShowModal(false)}
      >
        <View style={[styles.modal, { backgroundColor: redesign.bg.base }]}>
          <View style={[styles.modalHeader, { borderBottomColor: redesign.card.border }]}>
            <TouchableOpacity
              style={styles.cancelButton}
              onPress={() => setShowModal(false)}
            >
              <Text style={[styles.cancelButtonText, { color: semantic.text.link }]}>Cancel</Text>
            </TouchableOpacity>

            <Text style={[styles.modalTitle, { color: redesign.text.primary }]}>Select Icon</Text>

            <View style={styles.placeholder} />
          </View>

          <ScrollView style={styles.modalContent} contentContainerStyle={styles.iconGrid}>
            {iconRows.map((row, rowIndex) => (
              <View key={rowIndex} style={styles.row}>
                {row.map(([iconKey, iconLabel]) => renderIconItem(iconKey, iconLabel))}
              </View>
            ))}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
});

IconSelector.displayName = 'IconSelector';

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 8,
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    minHeight: 48,
  },
  selectedIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  selectedLabel: {
    flex: 1,
    fontSize: 16,
  },
  errorText: {
    fontSize: 14,
    marginTop: 4,
  },
  dropdownCard: {
    overflow: 'hidden',
  },
  modal: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  cancelButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  cancelButtonText: {
    fontSize: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  placeholder: {
    width: 60, // Same width as cancel button for centering
  },
  modalContent: {
    flex: 1,
    padding: 16,
  },
  iconGrid: {
    paddingBottom: 20,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  iconItem: {
    flex: 1,
    alignItems: 'center',
    padding: 16,
    marginHorizontal: 4,
    borderRadius: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  iconLabel: {
    fontSize: 14,
    textAlign: 'center',
  },
  iconLabelSelected: {
    fontWeight: '500',
  },
});
